import { Component, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { User } from '@models/models';
import { finalize, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { MessageService } from '@services/helpers/message-service/message.service';
import { NewSubscriptionServiceDataProvider } from '@services/subscription/subscription.service';
import {
	CommunicationEvents,
	PlanItemDescription,
	SubscriptionActions,
	SubscriptionStatus,
	SubscriptionStatusResponse
} from '@models/subscription.model';
import { IframeService } from '@services/helpers/iframe-service/iframe.service';
import { CancelSubscriptionModalComponent } from '@web-components/settings/profile/subscription-change/modals/cancel-subscription/cancel-subscription-modal';
import { SNModalService } from '@web-services/modal/snModal.service';
import { CancelSubscriptionConfirmModalComponent } from '@web-components/settings/profile/subscription-change/modals/cancel-subscription-confirm/cancel-subscription-confirm-modal';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'subscription-change',
	template: require('./subscription-change.html'),
	styles: [require('./subscription-change.scss')]
})
export class SubscriptionChangeComponent implements OnInit, OnDestroy {
	@Input() user: User;

	subscription: SubscriptionStatus;
	currentPlan: Array<string> = [];
	newPlan: Array<string> = [];
	actions: SubscriptionActions;
	priceComment: string;
	actionList: Array<string> = [];

	private iframeOpenEvent;
	private confirmPaymentOpenEvent;
	private checkoutPlanOpenEvent;
	private updateOpenEvent;
	private cancelSubscriptionModalRef: NgbModalRef;
	private disableButton: boolean;
	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private messageService: MessageService,
		private subscriptionService: NewSubscriptionServiceDataProvider,
		private iframeService: IframeService,
		private snModalService: SNModalService,
		private renderer: Renderer2
	) {}

	ngOnInit(): void {
		this.getSubscriptionStatus();
		this.attachIframeEvents();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.unsubscribeIframeEvents();
	}

	showCancelSubscriptionModal(): void {
		this.snModalService
			.openModal(CancelSubscriptionModalComponent)
			.result.then(() => {
				this.showNext();
			});
	}

	showPlanChooseModal(): void {
		this.subscriptionService.setChoosePlanOpenModalStatus({
			state: true,
			payload: null
		});
	}

	loadIframeModal(url: string): void {
		this.disableButton = true;
		this.iframeService.setUrlIframe(url);
	}

	cancelSubscription(feedback: string): void {
		this.sendFeedback(feedback);
		this.cancelUserSubscription();
	}

	private attachIframeEvents(): void {
		this.onEditBillingModalIframeOpen();
		this.onConfirmPaymentOpen();
		this.onCheckoutOpenEvent();
		this.onUpdatePaymentClose();
	}

	private showNext(): void {
		this.cancelSubscriptionModalRef = this.snModalService.openModal(
			CancelSubscriptionConfirmModalComponent
		);
		this.snModalService
			.getModalData()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((feedback: string) => this.cancelSubscription(feedback));
	}

	private sendFeedback(feedback: string): void {
		if (!feedback) {
			feedback = `User with email - ${
				this.user.email
			} unsubscribed without feedback.`;
		}

		this.subscriptionService
			.sendFeedback(feedback)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe();
	}

	private cancelUserSubscription(): void {
		this.subscriptionService
			.cancelSubscription()
			.pipe(
				takeUntil(this.unsubscribe),
				map(
					(data: SubscriptionStatusResponse): SubscriptionStatus => data.data
				),
				finalize(() => {
					this.cancelSubscriptionModalRef.componentInstance.btnDisable = false;
					this.cancelSubscriptionModalRef.dismiss();
				})
			)
			.subscribe(
				(data: SubscriptionStatus) => this.successCancelSubscription(data),
				err => this.errorCancelSubscription(err)
			);
	}

	private errorCancelSubscription(error) {
		this.messageService.showDefaultErrorMessage(error);
	}

	private successCancelSubscription(data: SubscriptionStatus): void {
		this.subscriptionService.setSubscriptionData(data);
	}

	private getSubscriptionStatus(): void {
		this.subscriptionService.subscriptionData
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(data: SubscriptionStatus): void =>
					this.successGetSubscriptionStatus(data),
				(err): void => this.errorGetSubscriptionStatus(err)
			);
	}

	private successGetSubscriptionStatus(data: SubscriptionStatus): void {
		if (data && data.currentPlan && data.actions) {
			this.subscription = JSON.parse(JSON.stringify(data));
			this.currentPlan = Object.keys(this.subscription.currentPlan.properties);
			this.actions = this.subscription.actions;
			this.actionList = this.subscriptionService.createActionList(this.actions);
			if (this.subscription.priceComment) {
				this.priceComment = this.subscription.priceComment;
			}

			if (this.subscription.newPlan) {
				this.newPlan = Object.keys(this.subscription.newPlan.properties);
			} else {
				this.newPlan = [];
			}
		}
	}

	private errorGetSubscriptionStatus(err): void {
		this.messageService.showDefaultErrorMessage(err);
	}

	private unsubscribeIframeEvents(): void {
		this.iframeOpenEvent();
		this.confirmPaymentOpenEvent();
		this.updateOpenEvent();
		this.checkoutPlanOpenEvent();
	}

	private onEditBillingModalIframeOpen(): void {
		this.iframeOpenEvent = this.renderer.listen(
			'document',
			CommunicationEvents.billingUpdateOpen,
			(): void => {
				this.disableButton = false;
			}
		);
	}

	private onConfirmPaymentOpen(): void {
		this.confirmPaymentOpenEvent = this.renderer.listen(
			'document',
			CommunicationEvents.confirmPaymentOpen,
			() => {
				this.disableButton = false;
			}
		);
	}

	private onCheckoutOpenEvent(): void {
		this.checkoutPlanOpenEvent = this.renderer.listen(
			'document',
			CommunicationEvents.planCheckoutOpen,
			() => {
				this.disableButton = false;
			}
		);
	}

	private onUpdatePaymentClose(): void {
		this.updateOpenEvent = this.renderer.listen(
			'document',
			CommunicationEvents.updatePaymentOpen,
			(): void => {
				this.disableButton = false;
			}
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/settings/profile/subscription-change/subscription-change.ts